<script setup lang="ts">
import { useTagStore } from '~/stores/tag'

import type Tag from '~/entities/tag'
import type { Influencer, StatsV3Influencer } from '~/types/influencer'

type Props = {
  influencer: Influencer | StatsV3Influencer
  offset?: number
  selected: boolean
}

const props = withDefaults(defineProps<Props>(), {
  offset: 0,
})

const { t } = useI18n()
const { GET_TAGS_FROM_IDS } = useTagStore()

function computeMarkTags(): Tag[] {
  return (
    GET_TAGS_FROM_IDS(props.influencer.tags.identity.influencer_mark ?? []) ??
    []
  )
}

const priorityArray = ['free', 'new'] as const
const markTags = ref<Tag[]>(computeMarkTags())

const selectedTag = computed<Tag | undefined>(() => {
  let tag: Tag | undefined

  for (const tagName of priorityArray) {
    tag = markTags.value.find((tag) => {
      return tag.name === tagName
    })
    if (tag) return tag
  }
  return undefined
})

const tagClass = computed<string>(() => selectedTag.value?.name ?? '')

const tagText = computed<string>(() => {
  if (selectedTag.value)
    return t(`tags.influencer_mark.${selectedTag.value.name}`)
  else return 'undefined tag!'
})
</script>

<template>
  <span v-if="selectedTag">
    <slot v-bind="{ tagText, selectedTag, tagClass }">
      <span :class="{ [`${tagClass}`]: tagClass, selected }">{{
        tagText
      }}</span>
    </slot>
  </span>
</template>

<style lang="scss" scoped>
.free {
  @apply tw-text-pink-500;
}
.new {
  @apply tw-text-blue-500;
}
.premium {
  @apply tw-text-green-600;
}
.free,
.new,
.premium {
  @apply tw-text-xs tw-font-medium tw-uppercase;
  &.selected {
    @apply tw-text-gray-400;
  }
}
</style>
